/**
 * R-14 — the shell's reads and writes for experience tiers, scope homes and View as, on L2's routes
 * (api/src/settings/experience-routes.ts, api/src/auth/view-as-routes.ts). The one place their shapes
 * are named. Every read degrades to "nothing known" (Expert, no home, no persona) — today's behaviour —
 * so a route that is not built yet (501) or refused changes nothing on screen.
 */
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { appsQueryKey } from '@/features/apps/apps-api';
import type { DisclosureRow, Tier } from './tiers';

export interface ExperiencePreference {
  appSlug: string;
  tier: Tier | null;
  /** `user`: the person chose it; `instance_default`: the app setting for their roles */
  source: 'user' | 'instance_default' | null;
  offered: Tier[];
}

export interface HomeScope {
  kind: 'department' | 'service_domain' | 'enterprise';
  entityId: string | null;
  key: string;
  name: string;
  viaRole: string;
}

export interface AppHomeAnswer {
  app: string;
  scopes: HomeScope[];
  chosen: HomeScope | null;
  needsChoice: boolean;
  route: string | null;
}

export interface ViewAsState {
  /** false on a production profile and for anyone but an Administrator */
  allowed: boolean;
  active: boolean;
  role: string | null;
  /** the active persona's role in business words (L2, additive) */
  roleLabel?: string | null;
  label?: string | null;
  department: { id: string; key: string; name: string } | null;
  startedAt: string | null;
  realUser: { userId: string; displayName: string } | null;
  /** what an Administrator may preview: roles in business words and the departments (empty when not allowed) */
  personas?: { roles: { code: string; label: string }[]; departments: { id: string; key: string; name: string }[] };
}

export type ExperienceDefaults = Record<string, Record<string, Tier>>;

const PREF_KEY = ['cigs', 'experience'] as const;
const DISCLOSURE_KEY = ['cigs', 'disclosure'] as const;
const HOME_KEY = ['cigs', 'home'] as const;
const DEFAULTS_KEY = ['cigs', 'experience-defaults'] as const;
export const VIEW_AS_KEY = ['cigs', 'view-as'] as const;

const quiet = async <T>(load: () => Promise<T>, empty: T): Promise<T> => {
  try {
    return await load();
  } catch {
    return empty;
  }
};
const q = (app: string) => `app=${encodeURIComponent(app)}`;

/**
 * R-15 #882 — the cache ages (decision for the handoff): an answer is kept for repeated reads inside one
 * app session (PREF_SESSION_MS, five minutes); entering an app (the Apps portal card, a route into
 * /apps/<slug> or an app's screens) re-reads it when it is older than PREF_APP_ENTRY_MS (30 seconds); a
 * failed read is never cached — the shell falls back (resolveTier: Expert) and asks again on the next
 * entry; a tier written through the app replaces the answer at once (afterTierSaved); signing in as
 * someone else invalidates every read (AuthProvider).
 */
export const PREF_SESSION_MS = 5 * 60_000;
export const PREF_APP_ENTRY_MS = 30_000;

const loadPreference = (app: string) => apiFetch<ExperiencePreference>(`/me/preferences/experience?${q(app)}`);

export function preferenceQueryOptions(app: string, load: (app: string) => Promise<ExperiencePreference> = loadPreference) {
  return { queryKey: [...PREF_KEY, app] as const, staleTime: PREF_SESSION_MS, retry: false, queryFn: () => load(app) };
}

/** Whether entering an app must read the preference again: a failed read, none yet, or one older than the entry age. */
export function needsFreshRead(state: { dataUpdatedAt: number; isError: boolean }, now: number = Date.now()): boolean {
  return state.isError || state.dataUpdatedAt === 0 || now - state.dataUpdatedAt > PREF_APP_ENTRY_MS;
}

export function useExperiencePreference(app: string, enabled: boolean) {
  return useQuery<ExperiencePreference>({ ...preferenceQueryOptions(app), enabled });
}

/**
 * Before a tier switch: stop any preference read still in flight. One started before the write (the
 * shell's first read, or a refetch) used to land after it with the old tier and put the shell back
 * to Beginner — half a switch, or none (gate A12/A13, golden 3).
 */
export async function beforeTierSave(qc: QueryClient): Promise<void> {
  await qc.cancelQueries({ queryKey: PREF_KEY });
}

/**
 * After the write: the saved tier is the shell's at once (every L0 entry, L1 menu and shell action
 * reads this one value), then the preference, the apps, the disclosure rows and the home are read
 * again together so the shell settles on the server's answer — never a mix of old and new.
 */
export async function afterTierSaved(qc: QueryClient, app: string, pref: ExperiencePreference): Promise<void> {
  qc.setQueryData([...PREF_KEY, app], pref);
  await Promise.all(EXPERIENCE_READS.map((queryKey) => qc.invalidateQueries({ queryKey })));
}

/** Everything the shell's menus and actions are built from. */
export const EXPERIENCE_READS: readonly (readonly string[])[] = [PREF_KEY, appsQueryKey, DISCLOSURE_KEY, HOME_KEY];

export function useSetExperience(app: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tier: Tier) => {
      await beforeTierSave(qc);
      return apiFetch<ExperiencePreference>('/me/preferences/experience', { method: 'PUT', body: { appSlug: app, tier } });
    },
    onSuccess: (pref) => afterTierSaved(qc, app, pref),
  });
}

/** The registry rows for an app and the platform's own (L0 entries, shell actions). */
export function useDisclosureRows(app: string, enabled: boolean) {
  return useQuery<DisclosureRow[]>({
    queryKey: [...DISCLOSURE_KEY, app],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const one = (a: string) => quiet(async () => (await apiFetch<{ rows: DisclosureRow[] }>(`/disclosure?${q(a)}`)).rows, [] as DisclosureRow[]);
      const [mine, platform] = await Promise.all([one(app), app === 'platform' ? Promise.resolve([]) : one('platform')]);
      return [...mine, ...platform];
    },
  });
}

export function useSaveDisclosureRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ elementId, ...body }: { app: string; elementId: string; minTier: Tier; note: string | null; reason: string }) =>
      apiFetch<DisclosureRow>(`/admin/disclosure/${encodeURIComponent(elementId)}`, { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DISCLOSURE_KEY }),
  });
}

/** T-068: the caller's scopes from their grants and the app's home for the chosen one. */
export function useAppHome(app: string, enabled: boolean) {
  return useQuery<AppHomeAnswer | null>({
    queryKey: [...HOME_KEY, app],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () => quiet(() => apiFetch<AppHomeAnswer>(`/me/home?${q(app)}`), null),
  });
}

export function useRememberHome(app: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scopeKey: string) => apiFetch<AppHomeAnswer>('/me/home', { method: 'PUT', body: { app, scopeKey } }),
    onSuccess: async (home) => {
      await qc.cancelQueries({ queryKey: HOME_KEY });
      qc.setQueryData([...HOME_KEY, app], home);
    },
  });
}

/** The instance default tier per app and role (`*` = every other role). */
export function useExperienceDefaults(enabled: boolean) {
  return useQuery<ExperienceDefaults>({
    queryKey: DEFAULTS_KEY,
    enabled,
    queryFn: async () => (await apiFetch<{ apps: ExperienceDefaults }>('/app-settings/experience')).apps,
  });
}

export function useSaveExperienceDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { apps: ExperienceDefaults; reason: string }) => apiFetch<{ apps: ExperienceDefaults }>('/app-settings/experience', { method: 'PUT', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DEFAULTS_KEY });
      void qc.invalidateQueries({ queryKey: PREF_KEY });
    },
  });
}

const NO_PREVIEW: ViewAsState = { allowed: false, active: false, role: null, department: null, startedAt: null, realUser: null };

export function useViewAs(enabled: boolean) {
  return useQuery<ViewAsState>({
    queryKey: VIEW_AS_KEY,
    enabled,
    staleTime: 60_000,
    queryFn: () => quiet(() => apiFetch<ViewAsState>('/auth/view-as'), NO_PREVIEW),
  });
}

/**
 * Start or stop: the server audits both; every cached read belongs to the other person afterwards,
 * so the cache is reset (not just marked stale) before the caller moves on.
 */
export function useViewAsControl() {
  const qc = useQueryClient();
  const after = () => qc.resetQueries();
  const start = useMutation({
    mutationFn: (body: { role: string; departmentId: string | null }) => apiFetch<ViewAsState>('/auth/view-as', { method: 'POST', body }),
    onSuccess: after,
  });
  const stop = useMutation({ mutationFn: () => apiFetch<ViewAsState>('/auth/view-as', { method: 'DELETE' }), onSuccess: after });
  return { start, stop };
}
